import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './PublicUserProfile.css';

const API = process.env.REACT_APP_API_URL;

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const renderPostText = (text) => {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) =>
    /^@\w+$/.test(part)
      ? <span key={i} className="qs-mention">{part}</span>
      : part
  );
};

const PublicUserProfile = ({ username, currentUser, onBack, onFollowChanged, onViewProfile }) => {
  const { t } = useTranslation();
  const [profile, setProfile]     = useState(null);
  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [followStatus, setFollowStatus] = useState('none');
  const [followLoading, setFollowLoading] = useState(false);

  const token = () => localStorage.getItem('access_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/profile/${username}/`,
        token() ? { headers: authHeader() } : {}
      );
      setProfile(r.data);
      setFollowStatus(r.data.follow_status || 'none');
    } catch (e) {
      setError(e.response?.data?.error || t('pp_not_found'));
    } finally {
      setLoading(false);
    }
  }, [username]);

  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const r = await axios.get(`${API}/users/${username}/qs/`,
        token() ? { headers: authHeader() } : {}
      );
      setPosts(r.data);
    } catch {
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!profile) return;
    const canSee = profile.is_public || followStatus === 'following' || followStatus === 'self';
    if (canSee) fetchPosts();
    else setPosts([]);
  }, [profile, followStatus]);

  const handleFollow = async () => {
    if (followLoading) return;
    setFollowLoading(true);
    try {
      if (followStatus === 'none') {
        const r = await axios.post(`${API}/follow/${username}/`, {}, { headers: authHeader() });
        setFollowStatus(r.data.status);
        setProfile(prev => prev ? {
          ...prev,
          followers_count: prev.followers_count + (r.data.status === 'following' ? 1 : 0),
        } : prev);
      } else {
        await axios.delete(`${API}/follow/${username}/`, { headers: authHeader() });
        const wasFollowing = followStatus === 'following';
        setFollowStatus('none');
        setProfile(prev => prev ? {
          ...prev,
          followers_count: prev.followers_count - (wasFollowing ? 1 : 0),
        } : prev);
        setPosts([]);
      }
      if (onFollowChanged) onFollowChanged();
    } catch {}
    finally { setFollowLoading(false); }
  };

  if (loading) return (
    <div className="pup-container">
      <button className="pup-back-btn" onClick={onBack}>← {t('back')}</button>
      <div className="pup-loading">{t('loading_dots')}</div>
    </div>
  );

  if (error) return (
    <div className="pup-container">
      <button className="pup-back-btn" onClick={onBack}>← {t('back')}</button>
      <div className="pup-error">{error}</div>
    </div>
  );

  const initials = (profile.full_name || profile.username || '?')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const canSeeFull = profile.is_public || followStatus === 'following' || followStatus === 'self';

  const FollowButton = () => {
    if (followStatus === 'self') return null;
    let label, cls;
    if (followStatus === 'following')    { label = t('unfollow');       cls = 'pup-btn-unfollow'; }
    else if (followStatus === 'pending') { label = t('request_sent');   cls = 'pup-btn-pending';  }
    else                                 { label = t('follow_action');  cls = 'pup-btn-follow';   }
    return (
      <button className={`pup-follow-btn ${cls}`} onClick={handleFollow} disabled={followLoading}>
        {label}
      </button>
    );
  };

  return (
    <div className="pup-container">
      <button className="pup-back-btn" onClick={onBack}>← {t('back')}</button>

      {/* Profile card */}
      <div className="pup-card">
        <div className="pup-avatar-wrap">
          {profile.photo_url
            ? <img src={profile.photo_url} alt={profile.username} className="pup-avatar-img" />
            : <div className="pup-avatar-placeholder">{initials}</div>
          }
        </div>

        <div className="pup-info">
          <h2 className="pup-username">@{profile.username}</h2>
          {profile.full_name && <p className="pup-fullname">{profile.full_name}</p>}
          {canSeeFull && profile.bio && <p className="pup-bio">{profile.bio}</p>}
          {!profile.is_public && (
            <span className="pup-private-badge">🔒 {t('private_profile')}</span>
          )}
          <div style={{ marginTop: 12 }}>
            <FollowButton />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="pup-stats">
        <div className="pup-stat">
          <span className="pup-stat-value">{profile.posts_count ?? '—'}</span>
          <span className="pup-stat-label">Q's</span>
        </div>
        <div className="pup-stat">
          <span className="pup-stat-value">{profile.followers_count ?? '—'}</span>
          <span className="pup-stat-label">{t('followers')}</span>
        </div>
        <div className="pup-stat">
          <span className="pup-stat-value">{profile.following_count ?? '—'}</span>
          <span className="pup-stat-label">{t('following')}</span>
        </div>
      </div>

      {/* Q's or private screen */}
      {canSeeFull ? (
        <div className="pup-qs-section">
          <h3 className="pup-qs-title">{t('pp_qs_title', { username: profile.username })}</h3>
          {postsLoading && <div className="pup-loading">{t('loading_dots')}</div>}
          {!postsLoading && posts.length === 0 && (
            <p className="pup-qs-empty">{t('pp_no_qs')}</p>
          )}
          {posts.map(post => (
            <div key={post.id} className="pup-post">
              <div className="pup-post-meta">
                <span className="pup-post-time">{timeAgo(post.created_at)}</span>
              </div>
              <p className="pup-post-text">{renderPostText(post.text)}</p>
              <div className="pup-post-stats">
                <span>♥ {post.likes_count || 0}</span>
                <span>💬 {post.comments_count || 0}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="pup-private">
          <div className="pup-private-icon">🔒</div>
          <h3>{t('pp_private_title')}</h3>
          <p>{t('pp_private_desc')}</p>
          <p className="pup-private-hint">{t('pp_private_hint')}</p>
          <FollowButton />
        </div>
      )}
    </div>
  );
};

export default PublicUserProfile;
